import { ChevronDown, Eye, FileText, Files, List } from 'lucide-react';
import { Button } from '@/components/ui/NewButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type ToolbarVolume = { index: number; label: string };

type BundleToolbarProps = {
  hasFiles: boolean;
  onPreview?: () => void;
  previewLoading?: boolean;
  onPreviewIndex?: () => void;
  previewIndexLoading?: boolean;
  /** Top-level volumes, in order; a per-volume preview item is shown for each when there are 2+. */
  volumes?: ToolbarVolume[];
  onPreviewVolume?: (volumeIndex: number, label: string) => void;
  previewVolumeLoadingIndex?: number | null;
  onGenerate?: () => void;
  /** Generate one file per volume; shown as a separate dropdown item when there are 2+ volumes. */
  onGenerateSplit?: () => void;
  generateLoading?: boolean;
  onGeneratedDocs: () => void;
};

export const BundleToolbar = ({
  hasFiles,
  onPreview,
  previewLoading,
  onPreviewIndex,
  previewIndexLoading,
  volumes = [],
  onPreviewVolume,
  previewVolumeLoadingIndex,
  onGenerate,
  onGenerateSplit,
  generateLoading,
  onGeneratedDocs,
}: BundleToolbarProps) => {
  const previewBlocked = !hasFiles;
  const showVolumePreviews = volumes.length > 1 && !!onPreviewVolume;
  const showGenerateSplit = volumes.length > 1 && !!onGenerateSplit;
  const hasPreviewItems = !!onPreview || !!onPreviewIndex || showVolumePreviews;
  const anyLoading =
    previewLoading ||
    previewIndexLoading ||
    generateLoading ||
    previewVolumeLoadingIndex != null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" loading={anyLoading} type="button">
          <Eye />
          Preview
          <ChevronDown className="opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onPreview && (
          <DropdownMenuItem disabled={previewBlocked || previewLoading} onSelect={() => onPreview?.()}>
            <Eye />
            Preview
          </DropdownMenuItem>
        )}
        {onPreviewIndex && (
          <DropdownMenuItem disabled={previewBlocked || previewIndexLoading} onSelect={() => onPreviewIndex?.()}>
            <List />
            Preview Index
          </DropdownMenuItem>
        )}
        {showVolumePreviews &&
          volumes.map((vol) => (
            <DropdownMenuItem
              key={vol.index}
              disabled={previewBlocked || previewVolumeLoadingIndex === vol.index}
              onSelect={() => onPreviewVolume?.(vol.index, vol.label)}
            >
              <Eye />
              {`Preview ${vol.label}`}
            </DropdownMenuItem>
          ))}
        {hasPreviewItems && onGenerate && <DropdownMenuSeparator />}
        {onGenerate && (
          <DropdownMenuItem disabled={previewBlocked || generateLoading} onSelect={() => onGenerate?.()}>
            <FileText />
            Generate
          </DropdownMenuItem>
        )}
        {showGenerateSplit && (
          <DropdownMenuItem disabled={previewBlocked || generateLoading} onSelect={() => onGenerateSplit?.()}>
            <Files />
            Generate per Volume
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onGeneratedDocs}>
          <Files />
          Generated Docs
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
